import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Alert,
  Box,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { pb, verifyDiscordServerMembership } from "../services/pocketbase";

const Register = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");

  const handleDiscordAuth = async () => {
    try {
      const authData = await pb.collection("users").authWithOAuth2({
        provider: "discord",
        scopes: ["identify", "email", "guilds.members.read"],
        createData: {
          emailVisibility: true,
        },
      });

      const isMember = await verifyDiscordServerMembership(authData);

      if (!isMember) {
        pb.authStore.clear();
        setError("You must be a member of our Discord server to register.");
        return;
      }

      // Update the user record with additional data
      try {
        await pb.collection("users").update(authData.record.id, {
          discord: authData.meta.id,
          emailVisibility: false,
        });
        navigate("/");
      } catch (err) {
        console.error("Error updating user:", err);
        throw err;
      }
    } catch (err) {
      console.error("Discord authentication error:", err);
      setError("Failed to authenticate with Discord. Please try again.");
      pb.authStore.clear();
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mt: 0,
      }}
    >
      <Paper
        sx={{
          p: 4,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          borderRadius: 2,
          width: "100%",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontFamily: "Refault, Georgia",
              fontStyle: "italic",
              fontSize: "4.5rem",
              fontWeight: 400,
              letterSpacing: "0em",
              textShadow: "0 9px rgba(0,0,0,.5)",
              marginBottom: "-2rem",
            }}
          >
            #TOG
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mt: 2,
              fontFamily: "Oswald, sans-serif",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              fontSize: "1.8rem",
              textTransform: "uppercase",
              textShadow: "0 7px rgba(0,0,0,.5)",
            }}
          >
            REGISTER
          </Typography>
        </Box>

        {error ? (
          <Alert
            severity="error"
            icon={false}
            sx={{
              mb: 2,
              "& .MuiAlert-message": {
                width: "100%",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            <span style={{ marginRight: "8px", fontSize: 20 }}>⚠️</span>
            {error}
            <span style={{ marginLeft: "8px", fontSize: 20 }}>⚠️</span>
          </Alert>
        ) : (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 2,
              }}
            >
              Please ensure you've joined before continuing.
            </Typography>
          </Box>
        )}

        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Button
            href="https://discord.gg/togx"
            target="_blank"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          >
            Join Discord Server
          </Button>
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={handleDiscordAuth}
            fullWidth
            sx={{
              backgroundColor: "#5865F2",
              py: 1.5,
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: "#4752C4",
              },
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09c-.01-.02-.04-.03-.07-.03c-1.5.26-2.93.71-4.27 1.33c-.01 0-.02.01-.03.02c-2.72 4.07-3.47 8.03-3.1 11.95c0 .02.01.04.03.05c1.8 1.32 3.53 2.12 5.24 2.65c.03.01.06 0 .07-.02c.4-.55.76-1.13 1.07-1.74c.02-.04 0-.08-.04-.09c-.57-.22-1.11-.48-1.64-.78c-.04-.02-.04-.08-.01-.11c.11-.08.22-.17.33-.25c.02-.02.05-.02.07-.01c3.44 1.57 7.15 1.57 10.55 0c.02-.01.05-.01.07.01c.11.09.22.17.33.26c.04.03.04.09-.01.11c-.52.31-1.07.56-1.64.78c-.04.01-.05.06-.04.09c.32.61.68 1.19 1.07 1.74c.03.01.06.02.09.01c1.72-.53 3.45-1.33 5.25-2.65c.02-.01.03-.03.03-.05c.44-4.53-.73-8.46-3.1-11.95c-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.83 2.12-1.89 2.12z" />
            </svg>
            Register with Discord
          </Button>
        </Box>

        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{" "}
            <Button color="primary" onClick={() => navigate("/login")}>
              Login
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register;
